import { useFormik, FormikProps } from "formik";
import * as Yup from "yup";
import { TripFormValues, Trips } from "../types/types";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { updateTrips } from "services/createTripService";
import { toast } from "react-toastify";

const useTripForm = (tripData: Trips, id: string, refetchData: () => void) => {
  const userDetails =
    useSelector((state: RootState) => state.appLayout.userDetails) ||
    ({} as any);

  const initialValues: TripFormValues = {
    title: tripData?.title || "",
    intro: tripData?.intro || "",
    merchantId: userDetails?.userId,
    // Older trips predate tripScope; default them to "domestic" (all legacy data is India).
    tripScope: tripData?.tripScope || "domestic",
    location: tripData?.location || {
      destination: [{ country: "", state: "", district: "" }],
      startLoc: [{ country: "", state: "", district: "" }],
      endLoc: [{ country: "", state: "", district: "" }],
    },
    minimumAge: tripData?.minimumAge || 18,
    maximumAge: tripData?.maximumAge || 20,
    group: tripData?.group || "",
    tripType: tripData?.tripType || "",
    faq: tripData?.faq || [{ id: 1, question: "", answer: "" }],
    descriptionSections: tripData?.descriptionSections || [
      { id: 1, heading: "", subHeading: "", description: "" },
    ],
    tripApprovalStatus:
      tripData?.tripApprovalStatus === "In-Review"
        ? "In-Review"
        : tripData?.tripApprovalStatus,
    comments: tripData?.comments || "",
    slotCount: tripData?.slotCount || 0,
    nights: tripData?.nights || 0,
    days: tripData?.days || 0,
    stayDetails: tripData?.stayDetails || [],
    noStayDetails: tripData?.noStayDetails || false,
    youtube: tripData?.youtube || [],
    inclusion: tripData?.inclusion ?? [
      { id: 0, label: "Transportation", description: "" },
      {
        id: 1,
        label: "Food",
        description: "",
      },
      { id: 2, label: "Accommodation", description: "" }
    ],
    weekend: tripData?.weekend || false,
    exclusion: tripData?.exclusion || "",
    thingsToCarry: tripData?.thingsToCarry || ""
  };

  // Domestic trips require country/state/district; international trips are country-only.
  const locationItemSchema = (requireStateDistrict: boolean) =>
    Yup.object({
      country: Yup.string().trim().required("Country is required"),
      state: requireStateDistrict
        ? Yup.string().trim().required("State is required")
        : Yup.string().nullable(),
      district: requireStateDistrict
        ? Yup.string().trim().required("District is required")
        : Yup.string().nullable(),
    });

  // International start/end need country + state + auto-selected airport (no district).
  const intlStartEndItem = Yup.object({
    country: Yup.string().trim().required("Country is required"),
    state: Yup.string().trim().required("State is required"),
    airport: Yup.string().trim().required("Airport is required"),
    district: Yup.string().nullable(),
  });

  // Domestic: country (India) + state + district for all three location groups.
  const domesticLocation = Yup.object({
    destination: Yup.array().of(locationItemSchema(true)),
    startLoc: Yup.array().of(locationItemSchema(true)),
    endLoc: Yup.array().of(locationItemSchema(true)),
  });

  // International: destination = country only; start/end = country + state + airport.
  const internationalLocation = Yup.object({
    destination: Yup.array().of(locationItemSchema(false)),
    startLoc: Yup.array().of(intlStartEndItem),
    endLoc: Yup.array().of(intlStartEndItem),
  });

  const validationSchema = Yup.object({
    title: Yup.string().trim().required("Trip title is required"),
    intro: Yup.string().trim().required("Trip intro is required"),
    tripScope: Yup.string().trim().required("Trip scope is required"),
    location: Yup.object().when("tripScope", {
      is: "international",
      then: () => internationalLocation,
      otherwise: () => domesticLocation,
    }),
    minimumAge: Yup.number()
      .required("Minimum age is required")
      .min(1, "Minimum age should be at least 1"),
    maximumAge: Yup.number()
      .required("Maximum age is required")
      .max(90, "Maximum age cannot exceed 90")
      .moreThan(
        Yup.ref("minimumAge"),
        "Maximum age must be greater than minimum age"
      ),
    group: Yup.string().trim().required("Group type is required"),
    tripType: Yup.string().trim().required("Trip type is required"),
    faq: Yup.array().of(
      Yup.object({
        question: Yup.string().trim().required("FAQ question is required"),
        answer: Yup.string().trim().required("FAQ answer is required"),
      })
    ),
    descriptionSections: Yup.array().of(
      Yup.object({
        heading: Yup.string().trim().required("Heading is required"),
        subHeading: Yup.string().trim().required("Subheading is required"),
        description: Yup.string().trim().required("Description is required"),
      })
    ),
    nights: Yup.number().required("Nights is required"),
    days: Yup.number().required("Days is required"),
    // inclusion: Yup.array().of(
    //   Yup.object({
    //     id: Yup.number().required(),
    //     label: Yup.string().required(),
    //     description: Yup.string()
    //       .trim()
    //       .required("Inclusion description is required"),
    //   })
    // ),
    exclusion: Yup.string().trim().required("Exclusion is required")
  });

  const formik: FormikProps<TripFormValues> = useFormik<TripFormValues>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        const updatedValues = {
          ...values,
          updatedAt: new Date().toISOString(),
        };
        const response = await updateTrips(id, updatedValues);
        if (response.message === "Item updated successfully") {
          refetchData();
        }
      } catch (error) {
        toast.error("Failed to submit trip data");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return { formik };
};

export default useTripForm;
